import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaTelegramPlane,
  FaShieldAlt,
  FaCloud,
  FaExternalLinkAlt,
  FaGraduationCap,
  FaBolt,
} from 'react-icons/fa';
import './CEO.css';

const publicFile = (filename) =>
  `${process.env.PUBLIC_URL}/${encodeURIComponent(filename)}`;

const CERTIFICATES = [
  {
    id: 'comptia-security-plus',
    title: 'CompTIA Security+ (ce)',
    subtitle: 'Industry-recognized cybersecurity credential demonstrating core security skills and risk awareness.',
    file: 'CompTIA Security+ ce certificate.pdf',
    Icon: FaShieldAlt,
  },
  {
    id: 'mulesoft-developer',
    title: 'MuleSoft Certified Developer',
    subtitle: 'Salesforce integration — API-led connectivity and enterprise integration expertise.',
    file: 'Cert_MuleSoftDeveloper_Wosen_Eshetu.pdf',
    Icon: FaCloud,
  },
  {
    id: 'associate-of-science',
    title: 'Associate of Science',
    subtitle: 'Academic credential — Montgomery College associate degree.',
    file: 'associate-of-science.png',
    Icon: FaGraduationCap,
  },
  {
    id: 'electrical-engineering-bs',
    title: 'Electrical Engineering',
    subtitle: 'Bachelor of Science — University of Maryland.',
    file: 'electrical-engineering.png',
    Icon: FaBolt,
  },
];

/** Replace with real details — image: add `public/ceo-profile.jpg` (or update path below). */
const PROFILE = {
  companyName: 'Gebeta Tech',
  name: 'Wosen Eshetu',
  jobTitle: 'Chief Executive Officer',
  photoSrc: `${process.env.PUBLIC_URL}/CEOimg01.webp`,
  phones: ['+251 985896868', '+1 240 281 9099'],
  email: 'wes@mulewave.com',
  whatsappUrl: 'https://wa.me/12402819099',
  telegramUrl: 'https://t.me/Wosen_Eshetu',
};

const BIOGRAPHY = {
  title: 'Autobiography',
  intro: [
    'I am Wosen Eshetu (Wes), an Ethiopian-born engineer and the founder of Gebeta Technologies. I grew up in Addis Ababa in a modest family, and the change started when I moved to the United States to pursue a better future through education and hard work.',
    'Over the years, I built my career across electrical engineering, software development, and enterprise integration, working with global organizations like Salesforce, Leidos, and IBM. Each experience helped shape my belief that technology should solve real problems and create opportunities for others.',
    'That belief is what brought me back home. Today, I am building Gebeta Technologies with a clear mission\u2014to create a strong technology company in Ethiopia and help the next generation of Ethiopian talent grow, innovate, and lead.',
  ],
  sections: [
    {
      heading: 'Early Life and Foundations',
      paragraphs: [
        'I was born and raised in the vibrant city of Addis Ababa, Ethiopia, growing up in what I describe as an "average poor" family. My educational journey began at Kes Timhirt bet, where I learned the Ethiopian alphabet, followed by my primary education at Temnja Yazh Elementary School and secondary education at Bole Senior Secondary School. My life took a pivotal turn when I won the Diversity Visa (DV) lottery, providing me the opportunity to move to the United States and chase the "American Dream".',
      ],
    },
    {
      heading: 'Academic Pursuit and Engineering Roots',
      paragraphs: [
        'Upon arriving in the U.S., I focused on service, work-study, and rigorous academic preparation. I attended Montgomery College, where I notably won a NASA-sponsored Spectrograph Design Competition. In 2011, I earned my Associate degree from Montgomery College and my Bachelor of Science from the University of Maryland degrees in Electrical and Computer Engineering. My passion for continuous learning has led me to my current pursuit of a master’s degree in software development at Grand Canyon University.',
      ],
    },
    {
      heading: 'Professional Evolution: From Hardware to Integration',
      paragraphs: [
        'My career spans over a decade of technical expertise across various industry sectors. I began with a strong focus on electrical and hardware engineering:',
      ],
      bullets: [
        'Amphenol Advanced Sensors: Developed fluid quality sensors and temperature sensors.',
        'Teledyne Energy Systems: Designed power controllers for Stirling Engines with radioactive heat sources for NASA lunar rovers.',
        'Department of the Navy (Pentagon): Worked on critical spectrum reallocation projects.',
        'Alertus Technologies: Specialized in redesigning alerting electronic devices for EMC compliance.',
      ],
      afterBullets: [
        'As the industry evolved, I transitioned into a specialist role in application design and enterprise integration. I became a Salesforce Certified MuleSoft Developer and Architect, holding additional certifications in Microsoft SQL Server. My software career includes high-impact roles at:',
      ],
      bullets2: [
        'Salesforce: Integrated recruitment platforms with secured SFTP servers.',
        'Leidos: Served as a MuleSoft Architect, implementing API-led connectivity and secure GovCloud integrations for federal systems.',
        'IBM & EigenX: Managed complex integrations for CRM systems like HubSpot and registrar systems like Blackboard.',
      ],
    },
    {
      heading: 'Gebeta Technologies and the Vision for Ethiopia',
      paragraphs: [
        'Driven by a deep sense of purpose, identity, and culture, I embarked on an "Exodus to Ethiopia" to give back to my home country. I founded Gebeta Technologies a year ago with a mission to build the largest tech firm in Ethiopia.',
        'Today, Gebeta Technologies has grown to 15 employees and has successfully completed multiple international projects and two homegrown products. Our expansion strategy targets Bahir Dar as a key destination to capture global markets and build a thriving tech culture.',
        'My ultimate vision is to solve local problems through technological innovation while providing a platform for the next generation of Ethiopian talent. By identifying key talents and providing internships for fresh graduates, we aim to sprout future geniuses who are, above all, dedicated problem solvers.',
      ],
    },
  ],
};

function CEO() {
  const [photoFailed, setPhotoFailed] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);

  const visibleSections = bioExpanded ? BIOGRAPHY.sections : [];
  const hasMoreBio = BIOGRAPHY.sections.length > 0;

  return (
    <section className="ceo-page">
      <div className="ceo-inner">
        <Link to="/" className="ceo-back">
          <FaArrowLeft aria-hidden />
          Back to home
        </Link>

        <article className="ceo-card">
          <div className="ceo-photo-wrap">
            {!photoFailed ? (
              <img
                className="ceo-photo"
                src={PROFILE.photoSrc}
                alt={`${PROFILE.name}, ${PROFILE.jobTitle} at ${PROFILE.companyName}`}
                onError={() => setPhotoFailed(true)}
              />
            ) : (
              <div className="ceo-photo-placeholder" role="img" aria-label="Profile photo placeholder">
                Photo unavailable
                <span>Add ceo-profile.jpg or CEOimg01.webp to the public folder.</span>
              </div>
            )}
          </div>

          <div className="ceo-body">
            <p className="ceo-company">{PROFILE.companyName}</p>
            <h1 className="ceo-name">{PROFILE.name}</h1>
            <p className="ceo-role">{PROFILE.jobTitle}</p>

            <div className="ceo-biography">
              <h2 className="ceo-bio-title">{BIOGRAPHY.title}</h2>
              <div className="ceo-bio-intro-wrap">
                {BIOGRAPHY.intro.map((text, i) => (
                  <p key={`intro-${i}`} className="ceo-bio-intro">
                    {text}
                  </p>
                ))}
              </div>
              {visibleSections.map((section, index) => (
                <div key={section.heading} className="ceo-bio-section">
                  {index > 0 && <hr className="ceo-bio-rule" aria-hidden />}
                  <h3 className="ceo-bio-section-title">{section.heading}</h3>
                  {section.paragraphs.map((text, i) => (
                    <p key={`p-${section.heading}-${i}`}>{text}</p>
                  ))}
                  {section.bullets && (
                    <ul className="ceo-bio-list">
                      {section.bullets.map((item, i) => (
                        <li key={`b-${section.heading}-${i}`}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.afterBullets?.map((text, i) => (
                    <p key={`a-${section.heading}-${i}`}>{text}</p>
                  ))}
                  {section.bullets2 && (
                    <ul className="ceo-bio-list">
                      {section.bullets2.map((item, i) => (
                        <li key={`b2-${section.heading}-${i}`}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {hasMoreBio && (
                <button
                  type="button"
                  className="ceo-bio-readmore"
                  onClick={() => setBioExpanded((v) => !v)}
                  aria-expanded={bioExpanded}
                >
                  {bioExpanded ? 'Show less' : 'Read more...'}
                </button>
              )}
            </div>

            <div className="ceo-certs">
              <h2 className="ceo-certs-heading">Certifications</h2>
         
              <div className="ceo-certs-grid">
                {CERTIFICATES.map((cert) => {
                  const href = publicFile(cert.file);
                  const Icon = cert.Icon;
                  return (
                    <article key={cert.id} className="ceo-cert-card">
                      <div className="ceo-cert-accent" aria-hidden />
                      <div className="ceo-cert-inner">
                        <div className="ceo-cert-head">
                          <div className="ceo-cert-icon-wrap" aria-hidden>
                            <Icon className="ceo-cert-icon" />
                          </div>
                          <div className="ceo-cert-head-text">
                            <h3 className="ceo-cert-title">{cert.title}</h3>
                            <p className="ceo-cert-subtitle">{cert.subtitle}</p>
                          </div>
                        </div>
                        <a
                          className="ceo-cert-btn ceo-cert-btn-primary"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaExternalLinkAlt aria-hidden />
                          View certificate
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="ceo-contact-grid">
              <div className="ceo-contact-row">
                <div className="ceo-contact-icon" aria-hidden>
                  <FaPhone />
                </div>
                <div className="ceo-contact-text">
                  <span className="ceo-contact-label">Phone</span>
                  <a href={`tel:${PROFILE.phones[0].replace(/\s/g, '')}`}>{PROFILE.phones[0]}</a>
                  <a href={`tel:${PROFILE.phones[1].replace(/\s/g, '')}`}>{PROFILE.phones[1]}</a>
                </div>
              </div>

              <div className="ceo-contact-row">
                <div className="ceo-contact-icon" aria-hidden>
                  <FaEnvelope />
                </div>
                <div className="ceo-contact-text">
                  <span className="ceo-contact-label">Email</span>
                  <a href={`mailto:${PROFILE.email}`}>{PROFILE.email}</a>
                </div>
              </div>
            </div>

            <div className="ceo-actions">
              <a className="ceo-btn-primary" href={PROFILE.whatsappUrl} target="_blank" rel="noopener noreferrer">
                <FaWhatsapp aria-hidden />
                WhatsApp
              </a>
              <a className="ceo-btn-outline" href={PROFILE.telegramUrl} target="_blank" rel="noopener noreferrer">
                <FaTelegramPlane aria-hidden />
                Telegram
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

export default CEO;
